/** Room setup on the local host (STORY-070): the LAN join address as text and QR, the three
 * steps, and hotspot guides per OS. Only meaningful when the session service runs on this
 * machine (the Teacher Computer app); the cloud answers 404 and the page says so. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { pickAddress, pickedAddress } from "../platform/joinOrigin";
import { TeacherChrome } from "../teacher/Chrome";
import { PairingCard } from "../teacher/PairingCard";
import { cacheCourse, getHostCache, getHostInfo, listCourses } from "../teacher/api";
import { Button, Qr } from "../ui";

export function Host() {
  const { t, i18n } = useTranslation();
  const host = useQuery({ queryKey: ["host-info"], queryFn: getHostInfo, retry: false, refetchInterval: 10_000 });
  const qc = useQueryClient();
  const cache = useQuery({ queryKey: ["host-cache"], queryFn: getHostCache, retry: false, enabled: !!host.data });
  const courses = useQuery({ queryKey: ["courses"], queryFn: listCourses, retry: false, enabled: !!host.data });
  const warm = useMutation({
    mutationFn: (id: string) => cacheCourse(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["host-cache"] }),
  });
  const cachedAt = (id: string) => cache.data?.courses.find((c) => c.course_id === id)?.fetched_at ?? null;
  const addresses = host.data?.addresses ?? [];
  const port = host.data?.port;
  // Which address the room is on. The host's first is a guess; the teacher's pick wins and is
  // the one every join link, QR and NFC tag uses (platform/joinOrigin).
  const [chosen, setChosen] = useState<string | null>(() => pickedAddress());
  const active = chosen && addresses.includes(chosen) ? chosen : addresses[0];
  const main = active && port ? `http://${active}:${port}/j` : undefined;
  const card = "rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";
  const platform = host.data?.platform ?? "";
  const guides = [
    { key: "guideMac", first: platform === "darwin" },
    { key: "guideWin", first: platform.startsWith("win") },
    { key: "guideLinux", first: platform.startsWith("linux") },
  ].sort((a, b) => Number(b.first) - Number(a.first));
  const when = (iso: string) => new Date(iso).toLocaleString(i18n.language, { dateStyle: "short", timeStyle: "short" });
  /** What this computer can still do when the server is out of reach; nothing to say when the
   * server is there and the courses are local anyway (STORY-112). */
  const cloudNote = !host.data ? null : !host.data.cloud ? (
    <>
      {t("host.noCloud")} <Link to="/t/settings" className="underline">{t("settings.link")}</Link>
    </>
  ) : host.data.online === false ? (
    t("host.cloudOffline")
  ) : host.data.local_content ? null : (
    t("host.offlineBody")
  );

  return (
    <TeacherChrome title={t("host.title")} wide>
      <header>
        <p className="text-sm text-[color:var(--ink-2)]"><Link to="/t" className="underline">{t("host.toCourses")}</Link></p>
        <p className="mt-1 max-w-prose text-sm text-[color:var(--ink-2)]">{t("host.lede")}</p>
      </header>
      {host.isError && <p className={card}>{t("host.noAddress")}</p>}
      {host.data && (
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_360px] md:items-start">
          <ol className="flex flex-col gap-3">
            <li className={card}>
              <h2 className="font-semibold">{t("host.step1")}</h2>
              <p className="text-sm text-[color:var(--ink-2)]">{t("host.step1Body")}</p>
              <a href="#hotspot" className="text-sm underline">{t("host.step1Guide")}</a>
            </li>
            <li className={`${card} flex flex-wrap items-start gap-4`}>
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold">{t("host.step2")}</h2>
                {main ? (
                  <p className="bignum mt-1 wrap-anywhere text-2xl" translate="no">{main}</p>
                ) : (
                  <p className="text-sm">{t("host.noAddress")}</p>
                )}
                <p className="text-sm text-[color:var(--ink-2)]">{t("host.step2Body")}</p>
                {host.data?.room && (
                  <p className="mt-1 text-sm text-[color:var(--ink-2)]">
                    {t("host.roomFindable", { room: host.data.room })}{" "}
                    <Link to="/t/settings" className="underline">{t("host.roomRename")}</Link>
                  </p>
                )}
                {addresses.length > 1 && (
                  /* Several addresses means a VPN or a second network: the first is a guess, so
                     the teacher picks the one the room is on and every join link follows it. */
                  <label className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-[color:var(--ink-2)]">{t("host.chooseAddress")}</span>
                    <select
                      className="field field-sm"
                      value={active ?? ""}
                      onChange={(e) => {
                        pickAddress(e.target.value);
                        setChosen(e.target.value);
                      }}
                    >
                      {addresses.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              {main && <Qr text={main} size={220} label={main} />}
            </li>
            <li className={card}>
              <h2 className="font-semibold">{t("host.step3")}</h2>
              <p className="text-sm text-[color:var(--ink-2)]">{t("host.step3Body")}</p>
            </li>
          </ol>
          <aside className="flex flex-col gap-3">
            <PairingCard className={card} />
            <section className={card}>
              <h2 className="font-semibold">{t("host.offline")}</h2>
              {/* With the courses local, the caching copy would mislead: there is nothing to prefetch. */}
              {cloudNote && <p className="mb-2 text-xs text-[color:var(--ink-2)]">{cloudNote}</p>}
              {host.data.local_content ? (
                <p className="text-sm">{t("host.localContent")}</p>
              ) : (
              <ul className="flex flex-col gap-1 text-sm">
                {(courses.data ?? []).map((c) => (
                  <li key={c.course_id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{c.title}</span>
                      {cachedAt(c.course_id) && <span className="text-xs text-[color:var(--ink-2)]">{t("host.cachedAt", { at: when(cachedAt(c.course_id)!) })}</span>}
                    </span>
                    <Button variant="secondary" loading={warm.isPending && warm.variables === c.course_id} disabled={!host.data?.cloud || host.data.online === false} onClick={() => warm.mutate(c.course_id)}>
                      {cachedAt(c.course_id) ? t("host.recache") : t("host.cache")}
                    </Button>
                  </li>
                ))}
                {courses.isError && cache.data && cache.data.courses.length > 0 && cache.data.courses.map((c) => (
                  <li key={c.course_id} className="text-xs text-[color:var(--ink-2)]">{t("host.cachedAt", { at: when(c.fetched_at) })}</li>
                ))}
              </ul>
              )}
              {!host.data.local_content && warm.data && warm.data.failed.length > 0 && <p className="mt-1 text-xs text-[color:var(--not-yet)]">{t("host.cacheFailed", { n: warm.data.failed.length })}</p>}
            </section>
          </aside>
          {/* the long card last and full width, one column per OS: neither column above waits on it */}
          <section id="hotspot" className={`${card} md:col-span-2`}>
            <h2 className="mb-2 font-semibold">{t("host.guides")}</h2>
            <ul className="grid gap-3 text-sm md:grid-cols-3">
              {guides.map((g) => (
                <li key={g.key} className={g.first ? "" : "text-[color:var(--ink-2)]"}>{t(`host.${g.key}`)}</li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-[color:var(--ink-2)]">{t("host.pending")}</p>
          </section>
        </div>
      )}
    </TeacherChrome>
  );
}
